import { TransitionEvent } from 'react';
import { Box } from '@mui/material';
import { GeneratedJobType, Message, RegenerateFuncType } from './types';
import { useAppSelector } from 'app/hooks';
import Output from 'components/Output';

type JobAdsProps = {
    item: GeneratedJobType | null;
    isGeneratingJob: boolean | undefined;
    regenerate: RegenerateFuncType;
    stopGenerating: ((unique: string) => void) | undefined;
    scrollToGroup: (groupId: string, scrollToLastGroupItem: boolean) => void;
    handleTransitionEnd: (groupId: string, event: TransitionEvent<Element>) => void;
    handleRewriteFragment: (
        data: {
            markedHtml: string;
            prompt: string;
        },
        messageId: string,
        groupId?: string | undefined,
    ) => Promise<void>;
};

export const JobAds = ({
    item,
    isGeneratingJob,
    regenerate,
    stopGenerating,
    scrollToGroup,
    handleTransitionEnd,
    handleRewriteFragment,
}: JobAdsProps) => {
    const { userSettings } = useAppSelector((state) => state.auth);
    const { conversationGroups, streamingValues } = useAppSelector((state) => state.global);

    const messagesLength = item?.messages?.filter((x) => !x.isHidden)?.length;

    const isDoneGenerating = (version: Message) => {
        const jobUniqKey = version.message_id;
        const streamingValue = streamingValues[jobUniqKey];
        return streamingValue?.done ?? true;
    };

    const isGeneratingForThis = (version: Message) => {
        const uniqueKey = version.message_id;
        return uniqueKey in streamingValues && !streamingValues[uniqueKey].done && isGeneratingJob;
    };

    return conversationGroups?.map((group) => (
        <Box
            className={`flex group ${group.isCollapsed ? 'collapsed' : ''}`}
            onTransitionEnd={(event) => handleTransitionEnd(group.id, event)}
            key={group.id}
        >
            {group.messagesIds.map((id, index) => {
                const version = item?.messages.find((message) => message.message_id === id);
                const isHidden = version?.isHidden;
                const isPreviousHidden = item?.messages[index - 1]?.isHidden;

                if (version) {
                    return (
                        <Box
                            key={id}
                            className={isHidden ? 'move-left' : ''}
                            sx={{
                                transition: 'margin .5s',
                                transitionDelay: isPreviousHidden ? '0.3s' : '0',
                            }}
                        >
                            <Output
                                item={version}
                                title={version?.title}
                                streamingValue={version.content}
                                regenerate={regenerate}
                                isGeneratingJob={isGeneratingJob}
                                messages={item?.messages}
                                stopGenerating={stopGenerating}
                                isGeneratingForThis={isGeneratingForThis(version)}
                                isDoneGeneratingForThis={isDoneGenerating(version)}
                                messagesLength={messagesLength}
                                userSelectedCountry={userSettings?.target_country}
                                userSelectedLanguage={userSettings?.output_language_code}
                                hiddenMessages={item?.hiddenMessages ?? []}
                                scrollToGroup={scrollToGroup}
                                handleRewriteFragment={handleRewriteFragment}
                            />
                        </Box>
                    );
                }
            })}
        </Box>
    ));
};
